import React, { FC, useEffect, useMemo } from "react"
import { graphql, useStaticQuery } from "gatsby"

import { BespokeComponentFragmentFragment, DelightLabsShareCarouselQueryQuery } from "@graphqlTypes"
import { isClientSide } from "@utils"

import BentoBoxBlock from "@components/lp/BentoBoxBlock/BentoBoxBlock"

import DelightLabsShareModal, { DELIGHT_LABS_SHARE_DATA, LS_IMAGE_KEY } from "./DelightLabsShareModal/DelightLabsShareModal"
import { DELIGHT_LABS_IMAGE_IDS } from "@constants"

import "./DelightLabsShareCarousel.scss"

type DelightLabsShareCarouselJsonPayload = {
  [k in DELIGHT_LABS_IMAGE_IDS]: DELIGHT_LABS_SHARE_DATA
}

const DelightLabsShareCarousel: FC<{
  contentful_id: BespokeComponentFragmentFragment["contentful_id"]
  jsonPayload: BespokeComponentFragmentFragment["jsonPayload"]
}> = ({
  contentful_id,
  jsonPayload,
}) => {
    const [currentModalId, setCurrentModalId] = React.useState<DELIGHT_LABS_IMAGE_IDS | null>(null)
    const { bentoBoxData } = useStaticQuery<DelightLabsShareCarouselQueryQuery>(graphql`
    query DelightLabsShareCarouselQuery {
      bentoBoxData :contentfulLpBentoBoxBlock(contentful_id: { eq: "1AJfsy7j3GJfQ4OchCx6wj" }) {
        ...BentoBoxBlockFragment
      }
    }
  `)

    const json = useMemo(() => {
      try {
        return JSON.parse(jsonPayload?.internal.content || "") as DelightLabsShareCarouselJsonPayload
      } catch (e) {
        console.error(`Failed to parse JSON Payload: DelightLabsShareCarousel (${contentful_id})`)
        return null
      }
    }, [jsonPayload])

    if (!bentoBoxData || !json) return null

    useEffect(() => {
      if (!isClientSide()) return

      const lsImageId = localStorage.getItem(LS_IMAGE_KEY) as DELIGHT_LABS_IMAGE_IDS

      if (lsImageId) {
        setCurrentModalId(lsImageId)
        window.localStorage.removeItem(LS_IMAGE_KEY)
      }
    }, [])

    const _items = bentoBoxData?.items?.map((item) => ({
      ...item,
      className: `delight-score--${item?.text?.raw?.match(/DELIGHT SCORE: ([0-9])/)[1]}`,
      onClick: () => {
        const imageId = item?.title?.split(": ")[1]?.replace(/\s/g, "_").toUpperCase()
        setCurrentModalId(imageId as DELIGHT_LABS_IMAGE_IDS)
      }
    }))

    if (!_items) return null

    return (
      <div className="DelightLabsShareCarousel">
        <BentoBoxBlock
          {...bentoBoxData}
          items={_items}
        />
        <DelightLabsShareModal
          currentModalData={currentModalId ? json[currentModalId] : null}
          onClose={() => setCurrentModalId(null)}
        />
      </div>
    )
  }

export default DelightLabsShareCarousel
